import { useState, useCallback, useRef, useEffect } from 'react';
import { FUNCTION_URL_SETTINGS } from '../config/appConfig';
import apiClient from '../utils/apiClient';
import { auth } from '../firebaseClient';

// Хук для управления экспортом через облачную функцию / серверный эндпоинт
export const useExport = (analysisResultData, uiStates, setErrorState, setStatusMessageState, currentStatusMessage) => {
  const {
    vkAnalysisResult,
    analysisEndTime,
  } = analysisResultData;

  const {
    startDate,
    endDate,
    postThreshold,
    analysisMode
  } = uiStates;

  const [exportStatus, setExportStatus] = useState({
    inProgress: false,
    type: null,
    progress: 0,
    fileName: null
  });

  const hideStatusTimerRef = useRef(null);

  const clearCompletionTimer = useCallback(() => {
    if (hideStatusTimerRef.current) {
      clearTimeout(hideStatusTimerRef.current);
      hideStatusTimerRef.current = null;
    }
  }, []);

  const apiBaseUrlFromEnv = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
  const fallbackBaseUrl = typeof window !== 'undefined'
    ? (window.location.hostname === 'localhost' ? 'http://localhost:3001' : window.location.origin)
    : 'http://localhost:3001';
  const resolvedApiBaseUrlRef = useRef(apiBaseUrlFromEnv || fallbackBaseUrl);

  const scheduleCompletion = useCallback((message?: string) => {
    setExportStatus(prev => ({ ...prev, progress: 100 }));
    if (message && setStatusMessageState) {
      setStatusMessageState(message);
    }
    clearCompletionTimer();
    hideStatusTimerRef.current = setTimeout(() => {
      setExportStatus(prev => ({ ...prev, inProgress: false }));
      hideStatusTimerRef.current = null;
    }, 3000);
  }, [clearCompletionTimer, setStatusMessageState]);

const resolveFunctionBaseUrl = useCallback(() => {
    const explicitUrl = (process.env.REACT_APP_EXPORT_FUNCTION_URL || '').trim();
    if (explicitUrl) {
      return explicitUrl;
    }

    const fallbackUrl = process.env.NODE_ENV === 'development'
      ? FUNCTION_URL_SETTINGS.EMULATOR
      : FUNCTION_URL_SETTINGS.DEPLOYED;

    if (fallbackUrl.startsWith('/')) {
      return `${resolvedApiBaseUrlRef.current}${fallbackUrl}`;
    }

    return fallbackUrl;
  }, []);

  useEffect(() => {
    return () => {
      if (hideStatusTimerRef.current) {
        clearTimeout(hideStatusTimerRef.current);
      }
    };
  }, []);

  const getChartFileName = useCallback((chartType) => {
    if (analysisMode === 'monthly_4weeks') {
      return chartType === 'ОМСУ' ? 'Нарушители ОМСУ за месяц' : 'Нарушители ИОМО за месяц';
    }

    return chartType === 'ОМСУ' ? 'Нет постов ОМСУ' : 'Нет постов ИОМО';
  }, [analysisMode]);

  const getExcelFileName = useCallback((type) => {
    if (analysisMode === 'monthly_4weeks') {
      return type === 'omsu' ? 'Нарушители ОМСУ за месяц' : 'Нарушители ИОМО за месяц';
    }

    return type === 'omsu' ? 'Нет постов ОМСУ' : 'Нет постов ИОМО';
  }, [analysisMode]);

  const triggerChartExport = useCallback(async (chartType, fileName, options = {}) => {
    const { silent = false } = options;
    const chartTypeParam = chartType === 'ОМСУ' ? 'omsu' : 'iogv';
    const resultsForType = vkAnalysisResult?.results?.[chartTypeParam];

    if (!resultsForType || !resultsForType.data || resultsForType.data.length === 0) {
      if (!silent && setStatusMessageState) setStatusMessageState(`Нет данных ${chartType} для экспорта графика.`);
      return;
    }

    if (!silent) {
      clearCompletionTimer();
      setExportStatus({
        inProgress: true,
        type: chartTypeParam,
        progress: 10,
        fileName
      });
      if (setStatusMessageState) {
        setStatusMessageState(`Запрос на экспорт графика ${chartType} (${fileName}.png)...`);
      }
    }

    try {
      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 20 }));
      }

      const dataToPass = {
        results: {
          [chartTypeParam]: resultsForType
        },
        analysisEndTime: analysisEndTime?.toISOString(),
        startDate,
        endDate,
        postThreshold,
        analysisMode,
        analysisMetadata: vkAnalysisResult?.analysisMetadata
      };

      let functionBaseUrl = resolveFunctionBaseUrl();
      if (!functionBaseUrl || functionBaseUrl.includes('YOUR_PROJECT_ID')) {
        console.error('URL функции экспорта не настроен!');
        if (!silent && setErrorState) setErrorState('URL функции экспорта не настроен.');
        if (!silent) {
          setExportStatus(prev => ({ ...prev, inProgress: false }));
        }
        if (silent) {
          throw new Error('URL функции экспорта не настроен.');
        }
        return;
      }

      const relativeBase = typeof window !== 'undefined' ? window.location.origin : resolvedApiBaseUrlRef.current;
      const url = functionBaseUrl.startsWith('/')
        ? new URL(functionBaseUrl, relativeBase)
        : new URL(functionBaseUrl);
      url.searchParams.set('chartType', chartTypeParam);
      url.searchParams.set('fileName', fileName);

      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 40 }));
      }
      const token = await auth.currentUser?.getIdToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify(dataToPass)
      });

      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 70 }));
      }

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Ошибка экспорта графика (HTTP ${response.status}): ${errorBody || 'Не удалось получить детали ошибки'}`);
      }

      const blob = await response.blob();
      if (blob.type !== 'image/png') {
        throw new Error('Сервер вернул не PNG файл.');
      }

      if ((blob.size ?? 0) === 0) {
        throw new Error('Получен пустой файл PNG. Попробуйте повторить экспорт позже.');
      }

      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 90 }));
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${fileName}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      if (!silent) {
        scheduleCompletion(`График ${fileName}.png успешно скачан.`);
      }
      return fileName;
    } catch (error) {
      console.error(`Ошибка при экспорте графика ${fileName}:`, error);
      if (!silent && setErrorState) {
        setErrorState(`Ошибка экспорта графика ${fileName}: ${error instanceof Error ? error.message : String(error)}`);
      }
      if (!silent) {
        clearCompletionTimer();
        setExportStatus(prev => ({ ...prev, inProgress: false }));
      }
      if (silent) {
        throw error;
      }
    }
  }, [vkAnalysisResult, analysisEndTime, startDate, endDate, postThreshold, analysisMode, setErrorState, setStatusMessageState, resolveFunctionBaseUrl, clearCompletionTimer, scheduleCompletion]);

  const exportOmsuChart = useCallback(() => {
    return triggerChartExport('ОМСУ', getChartFileName('ОМСУ'));
  }, [triggerChartExport, getChartFileName]);

  const exportIogvChart = useCallback(() => {
    return triggerChartExport('ИОМО', getChartFileName('ИОМО'));
  }, [triggerChartExport, getChartFileName]);

  const handleExportExcel = useCallback(async (type, options = {}) => {
    const { silent = false } = options;
    let dataToExport;
    const userFriendlyReportName = type === 'omsu' ? 'ОМСУ' : 'ИОГВ';

    if (type === 'omsu') dataToExport = vkAnalysisResult?.detailedNoncompliant?.omsu;
    else if (type === 'iogv') dataToExport = vkAnalysisResult?.detailedNoncompliant?.iogv;
    else {
      if (!silent && setErrorState) setErrorState('Неизвестный тип отчета для Excel.');
      return;
    }

    if (!dataToExport || dataToExport.length === 0) {
      if (!silent && setStatusMessageState) setStatusMessageState(`Нет данных ${userFriendlyReportName} для экспорта в Excel.`);
      return;
    }

    if (!silent) {
      clearCompletionTimer();
      setExportStatus({
        inProgress: true,
        type: `excel-${type}`,
        progress: 10,
        fileName: getExcelFileName(type)
      });

      if (setStatusMessageState) {
        setStatusMessageState(`Подготовка Excel файла для ${userFriendlyReportName}...`);
      }
    }

    try {
      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 30 }));
      }

      const response = await apiClient.post('/export/excel', {
        data: dataToExport,
        reportType: type,
        postThreshold,
        analysisMode,
        analysisMetadata: vkAnalysisResult?.analysisMetadata
      }, {
        responseType: 'blob'
      });

      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 70 }));
      }

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const defaultClientFilename = `${getExcelFileName(type)}.xlsx`;
      let finalDownloadFilename = defaultClientFilename;
      const disposition = response.headers['content-disposition'];

      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          let extractedFilename = matches[1].replace(/['"]/g, '');
          if (extractedFilename.toLowerCase().startsWith('utf-8\'\'')) {
            try {
              extractedFilename = decodeURIComponent(extractedFilename.substring(7));
            } catch (e) {
              console.warn('Could not decode UTF-8 filename from Content-Disposition:', e);
            }
          }
          finalDownloadFilename = extractedFilename;
        }
      }

      if (!silent) {
        setExportStatus(prev => ({ ...prev, progress: 90 }));
      }

      link.setAttribute('download', finalDownloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      if (!silent) {
        scheduleCompletion(`Файл ${finalDownloadFilename} успешно скачан.`);
      }
      return finalDownloadFilename;
    } catch (error) {
      console.error(`Ошибка при экспорте Excel для ${userFriendlyReportName}:`, error);
      if (!silent && setErrorState) setErrorState(`Ошибка экспорта Excel для ${userFriendlyReportName}: ${error instanceof Error ? error.message : String(error)}`);
      if (!silent) {
        clearCompletionTimer();
        setExportStatus(prev => ({ ...prev, inProgress: false }));
      }
      if (silent) {
        throw error;
      }
    }
  }, [vkAnalysisResult, postThreshold, analysisMode, setErrorState, setStatusMessageState, clearCompletionTimer, scheduleCompletion, getExcelFileName]);

  const handleExportOmsuExcel = useCallback(() => handleExportExcel('omsu'), [handleExportExcel]);
  const handleExportIogvExcel = useCallback(() => handleExportExcel('iogv'), [handleExportExcel]);

  const handleExportAll = useCallback(async () => {
    if (!vkAnalysisResult) {
      if (setStatusMessageState) setStatusMessageState('Нет данных для экспорта.');
      return;
    }

    const exportTasks = [];
    if (vkAnalysisResult.results?.omsu?.data?.length > 0) {
      exportTasks.push(() => triggerChartExport('ОМСУ', getChartFileName('ОМСУ'), { silent: true }));
    }
    if (vkAnalysisResult.results?.iogv?.data?.length > 0) {
      exportTasks.push(() => triggerChartExport('ИОМО', getChartFileName('ИОМО'), { silent: true }));
    }
    if (vkAnalysisResult.detailedNoncompliant?.omsu?.length > 0) {
      exportTasks.push(() => handleExportExcel('omsu', { silent: true }));
    }
    if (vkAnalysisResult.detailedNoncompliant?.iogv?.length > 0) {
      exportTasks.push(() => handleExportExcel('iogv', { silent: true }));
    }

    if (exportTasks.length === 0) {
      if (setStatusMessageState) setStatusMessageState('Нет данных для экспорта.');
      return;
    }

    clearCompletionTimer();
    setExportStatus({
      inProgress: true,
      type: 'all',
      progress: 5,
      fileName: 'Все материалы'
    });

    if (setStatusMessageState) {
      setStatusMessageState('Экспорт всех данных...');
    }

    const progressStep = 90 / exportTasks.length;
    let currentProgress = 5;

    try {
      for (const task of exportTasks) {
        await task();
        currentProgress += progressStep;
        const progressValue = Math.min(95, currentProgress);
        setExportStatus(prev => ({
          ...prev,
          progress: progressValue
        }));
      }
      scheduleCompletion('Экспорт всех данных завершен.');
    } catch (error) {
      console.error('Ошибка во время пакетного экспорта:', error);
      clearCompletionTimer();
      setExportStatus(prev => ({ ...prev, inProgress: false }));
      if (setErrorState) {
        setErrorState(`Критическая ошибка во время пакетного экспорта: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  }, [vkAnalysisResult, triggerChartExport, handleExportExcel, setStatusMessageState, clearCompletionTimer, scheduleCompletion, setErrorState, getChartFileName]);

  return {
    exportOmsuChart,
    exportIogvChart,
    handleExportOmsuExcel,
    handleExportIogvExcel,
    handleExportAll,
    exportStatus
  };
};

export default useExport;
